/**
 * Lógica pura de la portada del blog (Blog_Portada, D-20, D-27): qué posts se listan,
 * qué pestañas de categoría se pintan y con qué cuenta, y qué pestaña pide la URL.
 * Sin React, para que la página del servidor y la isla del cliente lean lo mismo.
 */
import { isDeindexed } from '@/lib/seo/deindexed-posts';
import { SLUGS_REDIRIGIDOS } from '@/lib/blog/redirecciones-blog.mjs';
import { ETIQUETAS_CATEGORIA_BLOG, type CategoriaBlog, type MetadatosPost } from './metadatos-post';

/** Un post tal como llega del listado de `/api/blog` (sin `content`). */
export interface PostPortada extends MetadatosPost {
    slug: string;
    title: string;
    published_at: string;
    author_type?: string;
    seo_indexable?: boolean;
    featured_image?: string | null;
}

export const ID_TODAS = 'todas';

// `investigacion` no es pestaña: el lienzo no la dibuja y su único post sigue en noindex hasta
// tener fuentes, así que no merece un hueco en la portada. Sus posts cuentan solo en «Todo».
const CATEGORIAS_CON_PESTANA = [
    'notas-clinicas',
    'ia-en-psicologia',
    'gestion-consulta',
    'cumplimiento-rgpd',
    'comparativas',
] as const satisfies readonly CategoriaBlog[];

export const PESTANAS_PORTADA = [ID_TODAS, ...CATEGORIAS_CON_PESTANA] as const;

export type IdPestanaPortada = (typeof PESTANAS_PORTADA)[number];

export interface PestanaPortada {
    id: IdPestanaPortada;
    etiqueta: string;
    cuenta: number;
}

// Los posts desindexados siguen accesibles por URL, pero fuera del listado dejan de absorber
// enlazado interno; los fusionados ya redirigen aunque la API aún los dé por publicados.
export function postsVisiblesPortada(posts: readonly PostPortada[] | null | undefined): PostPortada[] {
    return (posts ?? []).filter(
        (post) => !isDeindexed(post.slug, post.seo_indexable) && !SLUGS_REDIRIGIDOS.has(post.slug),
    );
}

/** La pestaña de categoría del post, o `null` si solo cuenta en «Todo». */
export function pestanaDelPost(post: Pick<PostPortada, 'categoria'>): IdPestanaPortada | null {
    const categoria = post.categoria;
    return (CATEGORIAS_CON_PESTANA as readonly string[]).includes(categoria ?? '')
        ? (categoria as IdPestanaPortada)
        : null;
}

function etiquetaDePestana(id: IdPestanaPortada): string {
    return id === ID_TODAS ? 'Todo' : ETIQUETAS_CATEGORIA_BLOG[id];
}

// Una pestaña vacía no se pinta: llevaría a una rejilla sin tarjetas (UI-SPEC §Interaction 8).
export function pestanasConCuenta(posts: readonly PostPortada[]): PestanaPortada[] {
    return PESTANAS_PORTADA.map((id) => ({
        id,
        etiqueta: etiquetaDePestana(id),
        cuenta: id === ID_TODAS ? posts.length : posts.filter((post) => pestanaDelPost(post) === id).length,
    })).filter((pestana) => pestana.cuenta > 0);
}

// La guía que destaca el lienzo. Si el post se renombra o cambia de categoría, el hueco pasa al
// notas-clinicas más reciente, para que nunca anuncie como guía algo que no lo es.
export const SLUG_GUIA_DESTACADA = 'notas-soap-psicologia-formato-ejemplo';

function masReciente(posts: readonly PostPortada[]): PostPortada | null {
    return posts.reduce<PostPortada | null>(
        (mejor, post) => (!mejor || Date.parse(post.published_at) > Date.parse(mejor.published_at) ? post : mejor),
        null,
    );
}

export function elegirDestacado(posts: readonly PostPortada[]): PostPortada | null {
    const notas = posts.filter((post) => post.categoria === 'notas-clinicas');
    return notas.find((post) => post.slug === SLUG_GUIA_DESTACADA) ?? masReciente(notas);
}

// La consulta la controla quien escribe la URL: solo vale un id de pestaña pintada, y nunca se
// refleja en el marcado tal cual.
export function categoriaDeConsulta(
    valor: string | null | undefined,
    pestanas: readonly Pick<PestanaPortada, 'id'>[],
): IdPestanaPortada {
    const pestana = pestanas.find((candidata) => candidata.id === valor);
    return pestana ? pestana.id : ID_TODAS;
}
